import { AfterViewInit, ChangeDetectorRef, Component, OnChanges, OnInit, SimpleChanges, ViewEncapsulation, ViewChild, OnDestroy, Input } from '@angular/core';
import { FlexmonsterPivot } from 'ngx-flexmonster';
import { Toolbar } from 'flexmonster';
import { environment } from 'app/environments/environment';
import { AREA, BASIN_QVSI_MEASURES, BasinQvsiProperties, createPivotMapping, csvToJSON, CsvParsedRow, EVENT_NET_II_COLUMN, getBasinQvsIPivotMeasures, getBasinQvsiPivotOptions, getBasinQvsiPivotSlice, getExistingProps, getKeyByProp, getResultsTabPivotSlice, getResultsTabPivotMeasures, mapTypeToString, PEAK_II, PROJECTED, PROJECTED_NET_II, PROJECTED_NET_II_SPACED, R_VALUE, RAINFAILL_TIME__PEAK, RAINFALL_DEPTH, ROLLING_PEAK_RAINFALL, SEWER_LENGTH, STORM_NET_II_COLUMN, STORM_PERIOD, TOTAL_EVENT_RDII, YVALUE_FIELD_SUFIX } from './basin-qvsi-pivot-table.constants'
import { Subscription } from 'rxjs';
import { SliicerService } from 'app/shared/services/sliicer.service';
import { Basin, SliicerCaseStudy } from 'app/shared/models/sliicer';
import { DateutilService } from 'app/shared/services/dateutil.service';
import { DatePipe, DecimalPipe } from '@angular/common';
import { SnackBarNotificationService } from 'app/shared/services/snack-bar-notification.service';
import { SNACK_BAR_NOTIFICATION_TIMEOUT } from 'app/shared/models/sliicer-data';
import { TranslateService } from '@ngx-translate/core';
import { BASIN_QVI_EXPORT_NAME, EXCLUDED_CHART_ITEMS, EXPORT_CSV_ID, EXPORT_EXCEL_ID, EXPORT_HTML_ID, EXPORT_IMG_ID, EXPORT_PDF_ID, EXPORT_VAULT_ID, TOOLBAR_CHARTS_TAB_ID, TOOLBAR_EXPORT_TAB_ID, TOOLBAR_FIELDS_TAB_ID, TOOLBAR_FORMAT_TAB_ID, TOOLBAR_FULLSCREEN_TAB_ID, TOOLBAR_GRID_TAB_ID, TOOLBAR_OPTIONS_TAB_ID, VAULT_ICON } from '../flexmonster.constant';
import { CustomerService } from 'app/shared/services/customer.service';
import { UnitOfMeasureType } from 'app/shared/constant';
import { BasinQvi, BasinQvsIProperties, Type } from '../../flow-monitor/basin-qvi-stats/basin-qvi-model';
import { BasinQvsISettingsOverrides } from 'app/shared/models/sliicer/overrides';
import { UiUtilsService } from 'app/shared/utils/ui-utils.service';
import { formatYIntercept as formatQviYIntercept } from '../../flow-monitor/q-i-graph/q-i-graph.utils';

const blankText = '(blank)';
const qvsiTypeKey = 'qvsiType';
const yInterceptKey = 'yIntercept';

@Component({
	selector: 'app-basin-qvsi-pivot-table',
	templateUrl: './basin-qvsi-pivot-table.component.html',
	styleUrls: ['./basin-qvsi-pivot-table.component.scss'],
	encapsulation: ViewEncapsulation.None,
})
export class BasinQvsiPivotTableComponent implements OnInit, OnDestroy, AfterViewInit, OnChanges {
	public license = environment.flexmonsterLicense;
	public report: Flexmonster.Report;
	@ViewChild('pivot') private pivotTable: FlexmonsterPivot;
	@Input() basinName: string;
	@Input() hideHeader = false;

	public isLoading = true;

	private caseStudyId: string;
	private customerId: number;
	private isBasinNameBound = false;

    public noData = false;
	private data: Record<string, string | number>[] = [];
	private rawBasinResults: BasinQvi[] = [];
	private isAllBasinsMode = false;

	private studyDateForExportNaming: string;
	private initialExportTabs: Flexmonster.ToolbarTab[];

	private units: UnitOfMeasureType;
	private subscriptions: Subscription[] = [];
	constructor(
		private sliicerService: SliicerService,
		private dateutilService: DateutilService,
		private datePipe: DatePipe,
		private numberPipe: DecimalPipe,
		private snackBarNotificationService: SnackBarNotificationService,
		private translate: TranslateService,
		private customerService: CustomerService,
		private uiUtils: UiUtilsService,
		private cdr: ChangeDetectorRef
	) { }

	ngOnInit(): void {
		this.units = this.getCustomerUnits();
	}

	ngAfterViewInit(): void {
		this.subscriptions.push(this.sliicerService.studyDetailsData$.subscribe((study: SliicerCaseStudy) => {
			this.caseStudyId = study.id;
			this.customerId = study.customerId;

			const format = this.dateutilService.getFormat().replace(/\//g, '');
			const start = new Date(study.config.startDate);
			const end = new Date(study.config.endDate);

			this.studyDateForExportNaming = `${this.datePipe.transform(start, format)}-${this.datePipe.transform(end, format)}`

			this.loadData();
			this.uiUtils.safeChangeDetection(this.cdr);
		}));
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (changes.basinName) {
			this.isBasinNameBound = true;
			if (!changes.basinName.firstChange && this.customerId && this.caseStudyId) {
				this.loadData();
			}
		}
	}

	public customizeToolbar(toolbar: Toolbar) {
		toolbar.showShareReportTab = true;

        this.pivotTable.flexmonster.on('reportcomplete', () => {
            this.pivotTable.flexmonster.refresh();
        });

        const tabs = this.getTabs(toolbar);

        toolbar.getTabs = function () {
            return tabs;
        };
	}

	private getTabs(toolbar: Toolbar, isGridView = false): Flexmonster.ToolbarTab[] {
		const tabs = toolbar.getTabs();

		const formatTab = tabs.find(v => v.id === TOOLBAR_FORMAT_TAB_ID);
		const optionsTab = tabs.find(v => v.id === TOOLBAR_OPTIONS_TAB_ID);
		const fieldsTab = tabs.find(v => v.id === TOOLBAR_FIELDS_TAB_ID);
		const fullScreenTab = tabs.find(v => v.id === TOOLBAR_FULLSCREEN_TAB_ID);

		const exportTab = tabs.find(v => v.id === TOOLBAR_EXPORT_TAB_ID);

		if (!this.initialExportTabs) {
			this.initialExportTabs = exportTab.menu;
		}

		const chartsTab = tabs.find(v => v.id === TOOLBAR_CHARTS_TAB_ID);
		const gridTab = tabs.find(v => v.id === TOOLBAR_GRID_TAB_ID);

		this.formatExportsTab(exportTab);

		gridTab.handler = this.onGridView.bind(this);

		chartsTab.menu = chartsTab.menu.filter(v => !EXCLUDED_CHART_ITEMS.includes(v.id));
		chartsTab.menu.forEach((item) => {
			item.handler = this.onChartView.bind(this);
		});

		return [
			exportTab,
			{ divider: true } as Flexmonster.ToolbarTab,
			chartsTab,
			gridTab,
			formatTab,
			optionsTab,
			fieldsTab,
			{ divider: true } as Flexmonster.ToolbarTab,
			fullScreenTab
		];
	}

	private removeBlanks() {
		this.pivotTable.flexmonster.customizeCell((cell: Flexmonster.CellBuilder, data: Flexmonster.CellData) => {
			if (data.label === blankText) {
				cell.text = '-';
			}

			const rowData = this.getRowData(data.rowIndex);
			const measureName = data.measure?.uniqueName;

			if (data.type !== 'value' || !measureName || typeof data.value !== 'number' || !rowData) {
				return;
			}

			if (measureName.includes(YVALUE_FIELD_SUFIX)) {
				const typeKey = this.isAllBasinsMode ? 'Type' : 'type';
				const decimals = this.getPrecisionByType(rowData[typeKey]);
				cell.text = data.value.toFixed(decimals);
				return;
			}

			if (!this.isAllBasinsMode && measureName === yInterceptKey) {
				cell.text = this.formatYInterceptCell(data.value, rowData);
			}
		});

	}

	private getRowData(rowIndex: number | undefined): Record<string, string | number> | null {
		if (rowIndex === undefined) {
			return null;
		}

		return this.data[rowIndex - 1] ?? null;
	}

	private formatYInterceptCell(value: number, rowData: Record<string, string | number>): string {
		return formatQviYIntercept(
			value,
			this.units,
			this.getQvsiType(rowData),
			(yIntercept: number, digitsInfo: string) => this.numberPipe.transform(yIntercept, digitsInfo),
		);
	}

	private getQvsiType(rowData: Record<string, string | number>): Type {
		const qvsiType = rowData[qvsiTypeKey];

		switch (qvsiType) {
			case Type.PeakPeak:
			case Type.PeakVolToPeak:
			case Type.VolumeStorm:
			case Type.VolVol:
				return qvsiType;
			default:
				return this.getQvsiTypeByLabel(rowData.type);
		}
	}

	private getQvsiTypeByLabel(type: string | number): Type {
		if (typeof type !== 'string') {
			return Type.VolVol;
		}

		if (type.includes(ROLLING_PEAK_RAINFALL)) {
			return Type.PeakPeak;
		}

		if (type.includes(RAINFAILL_TIME__PEAK)) {
			return Type.PeakVolToPeak;
		}

		if (type.includes(STORM_PERIOD)) {
			return Type.VolumeStorm;
		}

		return Type.VolVol;
	}

	private loadData(): void {
		if (!this.basinName && this.isBasinNameBound) {
			return;
		}

		this.isLoading = true;

		if (this.basinName) {
			this.loadBasinResults();
		} else {
			this.loadAllBasinsResults();
		}
	}

	private loadBasinResults(): void {
		this.sliicerService.getQviBasinResults(this.customerId, this.caseStudyId, this.basinName).subscribe(
			(jsonData: BasinQvi[]) => {
				if (!jsonData || !jsonData.length) {
					this.rawBasinResults = [];
					this.isLoading = false;
					this.noData = true;
					return;
				}

				this.rawBasinResults = jsonData;
				this.applyPersistedOverrides();
				const data = this.buildBasinRows(this.rawBasinResults);

				this.isAllBasinsMode = false;
				this.data = data.reverse();
				this.noData = false;
				this.generateBasinTable(data);
				this.isLoading = false;
			},
			() => { this.isLoading = false; }
		);
	}

	private applyPersistedOverrides(): void {
		if (!this.basinName || !this.rawBasinResults.length) {
			return;
		}
		const study: SliicerCaseStudy = this.sliicerService.caseStudyDetails.getValue();
		const persisted: BasinQvsISettingsOverrides[] = study && study.overrides && study.overrides.basinQVsISettings;
		if (!persisted || !persisted.length) {
			return;
		}
		for (const row of this.rawBasinResults) {
			if (row.basinName !== this.basinName) {
				continue;
			}
			const override = persisted.find((o: BasinQvsISettingsOverrides) =>
				o.basinName === row.basinName && o.mode === row.mode && o.type === row.type,
			);
			if (override) {
				row.result.useAlt = override.useAlt;
			}
		}
	}

	private buildBasinRows(jsonData: BasinQvi[]): BasinQvsIProperties[] {
		return jsonData.map((item: BasinQvi): BasinQvsIProperties => {
			const props: BasinQvsIProperties = {
				basinName: item.basinName,
				qvsiGroup: item.group,
				mode: item.mode,
				type: mapTypeToString(item.type),
				n: item.result.n,
				r2: item.result.r2,
				slope: item.result.useAlt ? item.result.altSlope : item.result.coeff2,
				yIntercept: item.result.useAlt ? 0 : item.result.coeff1,
				useAlt: item.result.useAlt ? 'Yes' : 'No',
				[qvsiTypeKey]: item.type,
				sewerLength: 0,
				area: 0
			};

			if (item.projections) {
				for (const projection of item.projections) {
					const value = item.result.useAlt ? projection.altProjection : projection.projection;
					props[`${projection.designStormName} ${PROJECTED} ${YVALUE_FIELD_SUFIX}`] = value;
				}
			}

			return props;
		});
	}

	public applyOverridesResult(updates: BasinQvsISettingsOverrides[]): void {
		if (this.isAllBasinsMode || !this.rawBasinResults.length || !updates.length) {
			return;
		}

		let changed = false;
		for (const row of this.rawBasinResults) {
			if (row.basinName !== this.basinName) {
				continue;
			}
			const update = updates.find((u: BasinQvsISettingsOverrides) =>
				u.basinName === row.basinName && u.mode === row.mode && u.type === row.type,
			);
			if (update && row.result.useAlt !== update.useAlt) {
				row.result.useAlt = update.useAlt;
				changed = true;
			}
		}

		if (!changed) {
			return;
		}

		const data = this.buildBasinRows(this.rawBasinResults);
		this.data = data.reverse();
		this.generateBasinTable(data);
		this.uiUtils.safeChangeDetection(this.cdr);
	}

	private loadAllBasinsResults(): void {
		this.isAllBasinsMode = true;
		this.rawBasinResults = [];
		this.sliicerService.getAllQviBasinResults(this.customerId, this.caseStudyId).subscribe(
			(csv: string) => {
				if (!csv) {
					this.isLoading = false;
					return;
				}

				const json: CsvParsedRow[] = csvToJSON(csv);

				if (!json || !json.length) {
					this.isLoading = false;
					return;
				}

				const data: Record<string, string | number>[] = json.filter(v => Object.keys(v).length > 0).map(item => {
					return Object.keys(item).reduce<Record<string, string | number>>((acc, k) => {
						const key = k.replace(/"/g, '').trim();
						const value = item[k];

						if (key.includes(SEWER_LENGTH) || key.includes(AREA)) {
							let formattedValue: string | number;
							if (typeof value === 'string') {
								formattedValue = Number(value.replace(/,/g, ''));
							} else if (value != null) {
								formattedValue = value;
							} else {
								formattedValue = '-';
							}
							acc[key] = formattedValue;
							return acc;
						}

						if (key.includes(R_VALUE)) {
							const unitsMatch = key.match(/\((.*?)\)$/);
							const units = (unitsMatch && unitsMatch[0]) ? unitsMatch[0].replace(/"/g, '').trim() : '';
							const designStormMatch = key.match(/^(.*?)\s+R-Value/);
							const designStormName = (designStormMatch && designStormMatch[1]) ? designStormMatch[1].replace(/"/g, '').trim() : '';
							const rValueKey = `${designStormName} ${PROJECTED} ${EVENT_NET_II_COLUMN} ${R_VALUE} ${units}`;
							acc[rValueKey] = value || '-';
							return acc;
						}

						if (!key.includes(PROJECTED_NET_II)) {
							acc[key] = value || '-';
							return acc;
						} else {
							const unitsMatch = key.match(/\((.*?)\)$/);
							const units = (unitsMatch && unitsMatch[0]) ? unitsMatch[0].replace(/"/g, '').trim() : '';
							const designStormMatch = key.match(/^(.*?)\s+Projected/);
							const designStormName = (designStormMatch && designStormMatch[1]) ? designStormMatch[1].replace(/"/g, '').trim() : '';
							const projectedNetIIKey = `${designStormName} ${PROJECTED_NET_II_SPACED} ${units}`;
							acc[projectedNetIIKey] = value || '-';
							return acc;
						}
					}, {});
				});

				this.data = data;
				if (!data || !data.length) {
					this.noData = true;
				} else {
					this.noData = false;
					this.generateResultsTable(data);
				}

				this.isLoading = false;
			},
			() => { this.isLoading = false; }
		);
	}

	private generateResultsTable(data: Record<string, string | number>[]): void {
		const mapping = createPivotMapping(data[0]);
		const isMetric = this.units === UnitOfMeasureType.METRIC;
		const isCfs = this.units === UnitOfMeasureType.CFS;
		const slice = getResultsTabPivotSlice(data[0], isMetric, isCfs);

		this.report = {
			options: getBasinQvsiPivotOptions(),
			dataSource: { data, mapping },
			slice: slice,
			formats: [
				{
					name: "3_decimal_format",
					decimalPlaces: 3,
					maxDecimalPlaces: 3,
					decimalSeparator: ".",
					thousandsSeparator: "",
				},
				{
					name: "2_decimal_format",
					decimalPlaces: 2,
					maxDecimalPlaces: 2,
					decimalSeparator: ".",
					thousandsSeparator: "",
				},
				{
					name: "1_decimal_format",
					decimalPlaces: 1,
					maxDecimalPlaces: 1,
					decimalSeparator: ".",
					thousandsSeparator: "",
				},
				{
					name: "0_decimal_format",
					decimalPlaces: 0,
					maxDecimalPlaces: 0,
				},
			],
		};
		this.isLoading = false;
		this.pivotTable.flexmonster.setReport(this.report);
		this.removeBlanks();
	}

	private generateBasinTable(data: BasinQvsIProperties[]): void {
		const isMetric = this.units === UnitOfMeasureType.METRIC;
		const slice = getBasinQvsiPivotSlice(data[0], isMetric);

		this.report = {
			options: getBasinQvsiPivotOptions(),
			dataSource: { data },
			slice: slice,
			formats: [
				{
					name: "4_decimal_format",
					decimalPlaces: 4,
					maxDecimalPlaces: 4,
					decimalSeparator: ".",
					thousandsSeparator: "",
				},
				{
					name: "3_decimal_format",
					decimalPlaces: 3,
					maxDecimalPlaces: 3,
					decimalSeparator: ".",
					thousandsSeparator: "",
				},
				{
					name: "2_decimal_format",
					decimalPlaces: 2,
					maxDecimalPlaces: 2,
					decimalSeparator: ".",
					thousandsSeparator: "",
				},
				{
					name: "1_decimal_format",
					decimalPlaces: 1,
					maxDecimalPlaces: 1,
					decimalSeparator: ".",
					thousandsSeparator: "",
				},
				{
					name: "0_decimal_format",
					decimalPlaces: 0,
					maxDecimalPlaces: 0,
				}
			],
		};
		this.isLoading = false;
		this.pivotTable.flexmonster.setReport(this.report);
		this.removeBlanks();
	}

	private formatExportsTab(tab: Flexmonster.ToolbarTab) {
		const exportTypes = [EXPORT_HTML_ID, EXPORT_CSV_ID, EXPORT_EXCEL_ID];

		tab.menu = tab.menu.filter(menuItem => {
			if (!exportTypes.includes(menuItem.id)) {
				return false;
			}

			this.setExportHandler(menuItem);

			return true;
		});

		tab.menu.unshift({
			title: 'To Vault(Raw)',
			id: EXPORT_VAULT_ID,
			icon: VAULT_ICON,
			handler: () => this.exportVaultHandler()
		});
	}

	private setExportHandler(menuItem: Flexmonster.ToolbarTab) {
		switch (menuItem.id) {
			case EXPORT_HTML_ID: {
				menuItem.handler = this.exportToHTMLhandler();
				break;
			}
			case EXPORT_EXCEL_ID: {
				menuItem.handler = this.exportToExcelHandler()
				break;
			}
			case EXPORT_CSV_ID: {
				menuItem.handler = this.exportToCsvHandler();
				break;
			}
		}
	}

	private exportToExcelHandler() {
		return () => this.pivotTable.flexmonster.exportTo('excel', {
			filename: `${BASIN_QVI_EXPORT_NAME}-${this.studyDateForExportNaming}`,
			excelSheetName: `${BASIN_QVI_EXPORT_NAME}-${this.studyDateForExportNaming}`,
		});

	}

	private exportToCsvHandler() {
		return () => this.pivotTable.flexmonster.exportTo('csv', {
			filename: `${BASIN_QVI_EXPORT_NAME}-${this.studyDateForExportNaming}`,
		});
	}

	private exportToHTMLhandler() {
		return () => this.pivotTable.flexmonster.exportTo('html', {
			filename: `${BASIN_QVI_EXPORT_NAME}-${this.studyDateForExportNaming}`,
		});
	}

	private exportVaultHandler() {
		const successMessage = this.translate.instant('SLIICER_TABLE.SLICER_SUMMARY.RESULTS.EXPORT_BASIN_SAVE_MESSAGE');
		const errorMessage = this.translate.instant('VAULT.VAULT_TELEMETRY.EXPORT.EXPORT_ERR_SNACKBAR_MSG');
		const dissmissText = this.translate.instant('COMMON.DISMISS_TEXT');

		this.isLoading = true;
		this.sliicerService
			.vaultExportBasicQvsiStatistics(this.customerId, this.caseStudyId)
			.subscribe(
				(res) => {
					this.isLoading = false;
					this.snackBarNotificationService.raiseNotification(
						successMessage,
						dissmissText,
						{
							duration: SNACK_BAR_NOTIFICATION_TIMEOUT,
						},
						true,
					);
				},
				(err) => {
					this.snackBarNotificationService.raiseNotification(errorMessage, dissmissText, {
						panelClass: 'custom-error-snack-bar',
					}, false);
					this.isLoading = false;
				},
			);
	}

	private onChartView(type: string) {
		this.pivotTable.flexmonster.showCharts(type, true);

		const report = this.pivotTable.flexmonster.getReport() as Flexmonster.Report;
		const item = this.data[0];
		const isMetric = this.units === UnitOfMeasureType.METRIC;
		const isCfs = this.units === UnitOfMeasureType.CFS;

		(report.slice as Flexmonster.Slice).measures = this.isAllBasinsMode
			? getResultsTabPivotMeasures(item, isMetric, isCfs)
			: getBasinQvsIPivotMeasures(item, isMetric);
		this.pivotTable.flexmonster.setReport(report);

		const tabs = this.getTabs(this.pivotTable.flexmonster.toolbar, false);
		this.pivotTable.flexmonster.toolbar.getTabs = function () { return tabs; }
		this.pivotTable.flexmonster.toolbar['create']();
	}

	private onGridView() {
		this.pivotTable.flexmonster.showGrid();

		const report = this.pivotTable.flexmonster.getReport() as Flexmonster.Report;
		const item = this.data[0];
		const isMetric = this.units === UnitOfMeasureType.METRIC;
		const isCfs = this.units === UnitOfMeasureType.CFS;

		(report.slice as Flexmonster.Slice).measures = this.isAllBasinsMode
			? getResultsTabPivotMeasures(item, isMetric, isCfs)
			: getBasinQvsIPivotMeasures(item, isMetric);
		this.pivotTable.flexmonster.setReport(report);

		const tabs = this.getTabs(this.pivotTable.flexmonster.toolbar, true);
		this.pivotTable.flexmonster.toolbar.getTabs = function () { return tabs; }
		this.pivotTable.flexmonster.toolbar['create']();
	}

	// #42240 #42203 #42239 update decimal places for Projected Y value
	// 	US Customary (MGD)
		// Peak I/I vs. Rolling Peak Rainfall Intensity - 3 decimal places
		// Peak I/I vs. Rainfall to Time of Peak I/I - 3 decimal places
		// Storm Period RDII vs. Rainfall Depth - 3 decimal places
		// Total Event RDII vs. Rainfall Depth - 3 decimal places
	// US Customary (CFS)
		// Peak I/I vs. Rolling Peak Rainfall Intensity - 3 decimal places
		// Peak I/I vs. Rainfall to Time of Peak I/I - 3 decimal places
		// Storm Period RDII vs. Rainfall Depth - 0 decimal places
		// Total Event RDII vs. Rainfall Depth - 0 decimal places
	// Metric
		// Peak I/I vs. Rolling Peak Rainfall Intensity - 1 decimal places
		// Peak I/I vs. Rainfall to Time of Peak I/I - 1 decimal places
		// Storm Period RDII vs. Rainfall Depth - 3 decimal places
		// Total Event RDII vs. Rainfall Depth - 3 decimal places
    private getPrecisionByType(type: string | number): number {
		if (typeof type !== 'string') {
			return 3;
		}

		if (this.units === UnitOfMeasureType.METRIC) {
			return type.includes(PEAK_II) ? 1 : 3;
		}

		if (this.units === UnitOfMeasureType.MGD) {
			return 3;
		}

		if (this.units === UnitOfMeasureType.CFS) {
			return type.includes(PEAK_II) ? 3 : 0;
		}

		return 3;
    }

	private getCustomerUnits(): UnitOfMeasureType {
		const unitsType = this.customerService.customer?.unitsType;

		switch (unitsType) {
			case UnitOfMeasureType.CFS:
			case UnitOfMeasureType.METRIC:
			case UnitOfMeasureType.MGD:
				return unitsType;
			default:
				return UnitOfMeasureType.MGD;
		}
	}

	ngOnDestroy() {
		this.subscriptions.forEach(v => v.unsubscribe());
	}
}
